/**
 * Map a job (office camelCase or field snake_case) into the daily-log form's
 * plain options. Kept apart from the screens so both surfaces offer the same
 * rows: every crew member, every owned unit, every rent-in (the form then
 * offers a rent-in only on its days).
 */
import type { PfDailyLog, PfFieldJobDetail, PfFieldLog, PfJobDetail } from '../../types/pipeline-facility';
import type { LogCrewOption, LogUnitOption, LogValues } from './LogEditor';

export function officeLogOptions(job: PfJobDetail): { crew: LogCrewOption[]; units: LogUnitOption[] } {
  return {
    crew: job.crew.map((c) => ({ userId: c.userId, name: c.userName ?? '—' })),
    units: [
      ...job.equipment.map((e) => ({
        key: `owned:${e.equipmentId}`,
        kind: 'owned' as const,
        equipmentId: e.equipmentId,
        rentInId: null,
        name: e.equipmentName ?? e.unitNumber ?? '—',
        detail: e.unitNumber,
      })),
      ...job.rentIns.map((r) => ({
        key: `rent_in:${r.id}`,
        kind: 'rent_in' as const,
        equipmentId: r.equipmentId,
        rentInId: r.id,
        name: r.description || r.vendorName,
        detail: [r.vendorName, r.unitNumber].filter(Boolean).join(' · ') || null,
        onRentOn: r.onRentOn,
        offRentOn: r.offRentOn,
      })),
    ],
  };
}

export function fieldLogOptions(job: PfFieldJobDetail): { crew: LogCrewOption[]; units: LogUnitOption[] } {
  return {
    crew: (job.crew ?? []).map((c) => ({ userId: c.user_id, name: c.user_name ?? '—' })),
    units: [
      ...(job.equipment ?? []).map((e) => ({
        key: `owned:${e.equipment_id}`,
        kind: 'owned' as const,
        equipmentId: e.equipment_id,
        rentInId: null,
        name: e.equipment_name ?? e.unit_number ?? '—',
        detail: e.unit_number,
      })),
      ...(job.rent_ins ?? []).map((r) => ({
        key: `rent_in:${r.id}`,
        kind: 'rent_in' as const,
        equipmentId: r.equipment_id,
        rentInId: r.id,
        name: r.description || r.vendor_name,
        detail: [r.vendor_name, r.unit_number].filter(Boolean).join(' · ') || null,
        onRentOn: r.on_rent_on,
        offRentOn: r.off_rent_on,
      })),
    ],
  };
}

export const officeLogValues = (log: PfDailyLog | null): LogValues | null =>
  log && {
    weather: log.weather,
    narrative: log.narrative,
    safetyNotes: log.safetyNotes,
    progressFromChainage: log.progressFromChainage,
    progressToChainage: log.progressToChainage,
    crew: log.crew.map((c) => ({ userId: c.userId, name: c.userName, hours: c.hours, standbyHours: c.standbyHours, notes: c.notes })),
    equipment: log.equipment.map((e) => ({
      kind: e.kind,
      equipmentId: e.equipmentId,
      rentInId: e.rentInId,
      name: e.unitName,
      hours: e.hours,
      standbyHours: e.standbyHours,
      hourMeterEnd: e.hourMeterEnd,
      equipmentCheckId: e.equipmentCheckId,
      notes: e.notes,
    })),
  };

export const fieldLogValues = (log: PfFieldLog | null): LogValues | null =>
  log && {
    weather: log.weather,
    narrative: log.narrative,
    safetyNotes: log.safety_notes,
    progressFromChainage: log.progress_from_chainage,
    progressToChainage: log.progress_to_chainage,
    crew: (log.crew ?? []).map((c) => ({ userId: c.user_id, name: c.user_name, hours: c.hours, standbyHours: c.standby_hours, notes: c.notes })),
    equipment: (log.equipment ?? []).map((e) => ({
      kind: e.kind,
      equipmentId: e.equipment_id,
      rentInId: e.rent_in_id,
      name: e.unit_name,
      hours: e.hours,
      standbyHours: e.standby_hours,
      hourMeterEnd: e.hour_meter_end,
      equipmentCheckId: e.equipment_check_id,
      notes: e.notes,
    })),
  };
